import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Target, TrendingUp, FileText, MessageSquare, Globe, BookOpen} from 'lucide-react';
import { useTheme } from '../hooks/useTheme';

const Services: React.FC = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [expandedService, setExpandedService] = useState<number | null>(0);

  const services = [
    {
      icon: Target,
      title: "Strategic Communications",
      description: "Create impactful communication strategies for sustainable development or business goals.",
      details: [
        "Customized plans for public health, agriculture, or corporate branding",
        "Behavior change campaigns for youth or community initiatives",
        "Crisis communication and reputation management",
        "Stakeholder engagement and partnership development"
      ],
      example: "Led 250+ projects at Serengeti Bytes for digital transformation in Tanzania.",
      color: "sunrise-orange"
    },
    {
      icon: MessageSquare,
      title: "Public Relations & Media",
      description: "Build your brand with expert PR services in Tanzania and media outreach.",
      details: [
        "Press releases and media relations for national coverage",
        "Crisis communication to protect your reputation",
        "Brand positioning and messaging strategy",
        "Media training and spokesperson development"
      ],
      example: "Enhanced engagement for Dar es Salaam BRT system.",
      color: "serengeti-gold"
    },
    {
      icon: Globe,
      title: "Digital Media & Marketing",
      description: "Boost your online presence with digital marketing in Tanzania.",
      details: [
        "Social media strategies for LinkedIn, Twitter, and Instagram",
        "SEO-optimized content for websites and blogs",
        "Digital campaign management and analytics",
        "Content creation and visual storytelling"
      ],
      example: "Increased DART's social engagement by 300%.",
      color: "deep-blue"
    },
    {
      icon: BookOpen,
      title: "Training & Coaching",
      description: "Empower teams with training in Tanzania on communication and digital skills.",
      details: [
        "Workshops on AI, data analytics, and content production",
        "Business coaching for youth agribusiness programs",
        "Digital communication skills development",
        "Strategic communication planning workshops"
      ],
      example: "Mentored 15,000+ youth in the BBT-YIA program.",
      color: "sunrise-orange"
    },
    {
      icon: FileText,
      title: "Project Documentation",
      description: "Showcase your impact with professional storytelling in Tanzania.",
      details: [
        "Audio-visual reports and case studies for donors",
        "Monitoring and evaluation (M&E) for project success",
        "Impact assessment and reporting",
        "Visual storytelling and multimedia content"
      ],
      example: "Documented nationwide BBT-YIA initiatives.",
      color: "serengeti-gold"
    }
  ];

  const toggleExpanded = (index: number) => {
    setExpandedService(expandedService === index ? null : index);
  };

  const getColorClasses = (color: string) => {
    switch (color) {
      case 'sunrise-orange':
        return {
          bg: isDark ? 'bg-sunrise-orange/20' : 'bg-sunrise-orange/10',
          text: 'text-sunrise-orange',
          border: isDark ? 'border-sunrise-orange/30' : 'border-sunrise-orange/20',
          hover: isDark ? 'hover:bg-sunrise-orange/30' : 'hover:bg-sunrise-orange/20'
        };
      case 'serengeti-gold':
        return {
          bg: isDark ? 'bg-serengeti-gold/20' : 'bg-serengeti-gold/10',
          text: 'text-serengeti-gold',
          border: isDark ? 'border-serengeti-gold/30' : 'border-serengeti-gold/20',
          hover: isDark ? 'hover:bg-serengeti-gold/30' : 'hover:bg-serengeti-gold/20'
        };
      case 'deep-blue':
        return {
          bg: isDark ? 'bg-deep-blue/20' : 'bg-deep-blue/10',
          text: 'text-deep-blue',
          border: isDark ? 'border-deep-blue/30' : 'border-deep-blue/20',
          hover: isDark ? 'hover:bg-deep-blue/30' : 'hover:bg-deep-blue/20'
        };
      default:
        return {
          bg: isDark ? 'bg-sunrise-orange/20' : 'bg-sunrise-orange/10',
          text: 'text-sunrise-orange',
          border: isDark ? 'border-sunrise-orange/30' : 'border-sunrise-orange/20',
          hover: isDark ? 'hover:bg-sunrise-orange/30' : 'hover:bg-sunrise-orange/20'
        };
    }
  };

  return (
    <section id="services" className="py-20 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fade-in-up">
          <h2 className={`text-4xl md:text-5xl font-light mb-6 ${
            isDark ? 'text-white' : 'text-charcoal'
          }`}>
            My <span className="gradient-text font-medium">Services</span>
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-sunrise-orange to-serengeti-gold mx-auto rounded-full mb-4"></div>
          <div className={`text-lg max-w-4xl mx-auto text-left ${
            isDark ? 'text-gray-400' : 'text-gray-600'
          }`}>
            <p className="mb-4">
              As a leading communications consultant in Tanzania, I offer tailored solutions to boost your organization's 
              visibility and impact. Based in Dar es Salaam, I specialize in strategic communications, public relations, 
              and digital media for government, NGOs, and businesses.
            </p>
            <p>
              Explore my comprehensive services below to discover how I can help transform your communication strategy 
              and drive meaningful results for your organization.
            </p>
          </div>
        </div>

        {/* Services Grid */}
        <div className="space-y-8">
          {services.map((service, index) => {
            const colorClasses = getColorClasses(service.color);
            return (
              <div 
                key={service.title}
                className={`glass border rounded-3xl p-6 lg:p-8 shadow-2xl transition-all duration-500 hover:scale-105 cursor-pointer ${
                  isDark ? 'glass-dark' : 'glass-light'
                } ${expandedService === index ? 'ring-2 ring-sunrise-orange/50' : ''}`}
                onClick={() => toggleExpanded(index)}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {/* Service Header */}
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-start gap-6 flex-1">
                    <div className={`p-5 rounded-2xl ${colorClasses.bg} ${colorClasses.border} border flex-shrink-0`}>
                      <service.icon size={36} className={colorClasses.text} />
                    </div>
                    <div className="flex-1">
                      <h3 className={`text-2xl lg:text-3xl font-semibold mb-4 ${
                        isDark ? 'text-white' : 'text-charcoal'
                      }`}>
                        {service.title}
                      </h3>
                      <p className={`text-lg leading-relaxed ${
                        isDark ? 'text-gray-300' : 'text-gray-600'
                      }`}>
                        {service.description}
                      </p>
                    </div>
                  </div>
                  
                  <button className={`p-3 rounded-full transition-transform duration-300 flex-shrink-0 ${
                    isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'
                  }`}>
                    {expandedService === index ? (
                      <ChevronUp size={28} className={isDark ? 'text-white' : 'text-gray-700'} />
                    ) : (
                      <ChevronDown size={28} className={isDark ? 'text-white' : 'text-gray-700'} />
                    )}
                  </button>
                </div>

                {/* Expanded Content */}
                {expandedService === index && (
                  <div className="mt-8 pt-8 border-t border-gray-300/20 animate-fade-in-up">
                    <div className="grid lg:grid-cols-2 gap-10">
                      {/* Service Details */}
                      <div>
                        <h4 className={`text-xl font-semibold mb-6 ${
                          isDark ? 'text-white' : 'text-charcoal'
                        }`}>
                          What I Offer
                        </h4>
                        <ul className="space-y-4">
                          {service.details.map((detail, detailIndex) => (
                            <li key={detailIndex} className="flex items-start gap-4">
                              <div className={`w-3 h-3 rounded-full mt-2 flex-shrink-0 ${colorClasses.text}`}></div>
                              <span className={`text-base leading-relaxed ${
                                isDark ? 'text-gray-300' : 'text-gray-600'
                              }`}>
                                {detail}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Example/Case Study */}
                      <div>
                        <h4 className={`text-xl font-semibold mb-6 ${
                          isDark ? 'text-white' : 'text-charcoal'
                        }`}>
                          Real Impact
                        </h4>
                        <div className={`p-6 rounded-2xl ${colorClasses.bg} ${colorClasses.border} border`}>
                          <p className={`text-base leading-relaxed ${colorClasses.text}`}>
                            <strong>Example:</strong> {service.example}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Call to Action */}
        <div className="mt-16 text-center">
          <div className={`glass border rounded-3xl p-8 lg:p-12 shadow-2xl ${
            isDark ? 'glass-dark' : 'glass-light'
          }`}>
            <h3 className={`text-2xl font-medium mb-4 ${
              isDark ? 'text-white' : 'text-charcoal'
            }`}>
              Ready to Transform Your Communications?
            </h3>
            <p className={`text-lg leading-relaxed mb-8 max-w-2xl mx-auto ${
              isDark ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Let's discuss how I can help your organization achieve its communication goals. 
              Contact me for a free consultation on your strategic communications needs.
            </p>
            <button 
              onClick={() => {
                const element = document.querySelector('#contact');
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              className={`glass border px-8 py-4 rounded-full transition-all duration-300 flex items-center justify-center gap-3 font-medium hover:scale-105 cursor-pointer mx-auto ${
                isDark 
                  ? 'glass-dark glass-hover-dark text-gray-100' 
                  : 'glass-light glass-hover-light text-slate-700'
              }`}
            >
              Get Started Today
              <TrendingUp size={20} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
